import React from "react";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import MfaIcon from "../../assets/new icons/user profile/SVG.svg";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import { AppCheckbox, fontFamily, useSharedValue } from "../Constants/constants";
import "../modal.css";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import { BASE_URL } from "../../App_Settings/app_settings";
import { InputBase } from "@mui/material";
import { getUserIdFromLocalStorage } from "../../API_Endpoints/APIEndpoints";
import {
  COUNTRY_CODES,
  getCountryOptions,
  validateMobileNumber,
  checkCountryCodeRequired,
} from "../../Utility/countryCodesUtil";

export default function UserModalComponent({
  show,
  handleClose,
  onSave,
  handleEdit,
  title,
  isSuperAdmin: propIsSuperAdmin,
}) {
  const { formatBusinessUnitLabel } = useSharedValue();
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [countryCode, setCountryCode] = React.useState("+44");
  const [mobileNumber, setMobileNumber] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [isSystemAdmin, setIsSystemAdmin] = React.useState(false);
  const [isMaker, setIsMaker] = React.useState(false);
  const [isChecker, setIsChecker] = React.useState(false);
  const [isSuspended, setIsSuspended] = React.useState(true);
  const [mfaEnabled, setMfaEnabled] = React.useState(false);
  const [mfaEmail, setMfaEmail] = React.useState(false);
  const [mfaMobile, setMfaMobile] = React.useState(false);
  const [maxConcurrentSessions, setMaxConcurrentSessions] = React.useState(1);
  const [selectedBusinessUnits, setSelectedBusinessUnits] = React.useState([]);
  const [businessUnits, setBusinessUnits] = React.useState([]);
  const [defaultBusinessUnitId, setDefaultBusinessUnitId] = React.useState(null);
  const [roles, setRoles] = React.useState([]);
  const [errors, setErrors] = React.useState({});
  const [organizations, setOrganizations] = React.useState([]);
  const [selectedOrganization, setSelectedOrganization] = React.useState(null);
  // Use the prop instead of local state
  const isSuperAdmin = propIsSuperAdmin || false;
  const [organizationSearchValue, setOrganizationSearchValue] = React.useState("");

  // Password Validation
  const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;

  const validateFields = () => {
    let fieldErrors = {};

    // First Name validation
    if (!firstName || !firstName.trim()) {
      fieldErrors.firstName = "First Name is required.";
    }

    // Last Name validation
    if (!lastName || !lastName.trim()) {
      fieldErrors.lastName = "Last Name is required.";
    }

    // Email validation
    if (!email || !email.trim()) {
      fieldErrors.email = "Email is required.";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        fieldErrors.email = "Please enter a valid email address.";
      }
    }

    // Mobile Number and Country Code validation

    if (!mobileNumber || !mobileNumber.trim()) {
      fieldErrors.mobileNumber = "Mobile Number is required.";
    } else {
      const countryCodeError = checkCountryCodeRequired(countryCode);

      if (countryCodeError && typeof countryCodeError === "object" && countryCodeError.hasError) {
        fieldErrors.mobileNumber = countryCodeError.errorMessage || "Country code is required.";
      } else if (countryCodeError && typeof countryCodeError === "string") {
        fieldErrors.mobileNumber = countryCodeError;
      } else if (
        !countryCodeError ||
        (typeof countryCodeError === "object" && !countryCodeError.hasError)
      ) {
        const mobileError = validateMobileNumber(mobileNumber, countryCode);

        if (mobileError && !mobileError.isValid) {
          fieldErrors.mobileNumber = mobileError.message;
        }
      }
    }

    // Password validation (only for new users)
    if (!handleEdit) {
      if (!password || !password.trim()) {
        fieldErrors.password = "Password is required.";
      } else if (!passwordRegex.test(password)) {
        fieldErrors.password =
          "Password must be 8+ characters with an uppercase letter, a number, and a special character.";
      }
    }

    // Business Units validation

    if (selectedBusinessUnits.length === 0) {
      fieldErrors.businessUnits = "At least one Client must be selected.";
    }

    // Organization validation for super admin

    if (isSuperAdmin && !selectedOrganization) {
      fieldErrors.organization = "Please select an organization.";
    }

    // Roles validation

    if (!isSystemAdmin && !isMaker && !isChecker) {
      fieldErrors.roles = "At least one Role must be selected.";
    }

    setErrors(fieldErrors);

    const isValid = Object.keys(fieldErrors).length === 0;

    return isValid;
  };

  const handleSaveClick = () => {
    // Always run validation to show errors
    const isValid = validateFields();

    if (!isValid) {
      console.log("❌ Validation failed, showing errors");
      return;
    }

    const assignedRoles = [];
    roles.forEach((role) => {
      if (
        (role.name === "Admin" && isSystemAdmin) ||
        (role.name === "Maker" && isMaker) ||
        (role.name === "Checker" && isChecker)
      ) {
        assignedRoles.push({ roleId: role.id });
      }
    });

    const activeValue = isSuspended;

    // Concatenate country code with mobile number if both exist
    const fullMobileNumber =
      mobileNumber && mobileNumber.trim()
        ? countryCode
          ? `${countryCode}${mobileNumber}`
          : mobileNumber
        : mobileNumber;

    const newRow = {
      firstName,
      lastName,
      email,
      mobileNumber: fullMobileNumber,
      password: password,
      activeValue,
      mfaEnabled,
      mfaEmail,
      mfaMobile,
      maxConcurrentSessions: parseInt(maxConcurrentSessions) || 1,
      roles: assignedRoles,
      businessUnits: selectedBusinessUnits.map((unit) => ({
        businessUnitId: unit.id,
        isDefault: unit.id === defaultBusinessUnitId,
      })),
      organizationId: selectedOrganization ? selectedOrganization.id : undefined,
      // Include organization object for UI consistency
      organization: selectedOrganization,
    };

    handleClose();
    onSave(newRow);
  };

  React.useEffect(() => {
    const initializeData = async () => {
      try {
        // Use the prop value for super admin check
        const isSuperAdminUser = isSuperAdmin;

        // Fetch roles
        const rolesResponse = await fetch(`${BASE_URL}/RolePermission/roles`, {
          headers: { Authorization: `Bearer ${getUserIdFromLocalStorage().token}` },
        });
        const rolesData = await rolesResponse.json();
        setRoles(rolesData);

        // Fetch organizations if super admin
        if (isSuperAdminUser) {
          const userDataJSON = localStorage.getItem("token");
          if (userDataJSON) {
            const _userData = JSON.parse(userDataJSON);
            const orgResponse = await fetch(
              `${BASE_URL}/Organization/${getUserIdFromLocalStorage().userId}/${getUserIdFromLocalStorage().organizationId}`,
              {
                headers: { Authorization: `Bearer ${getUserIdFromLocalStorage().token}` },
              }
            );
            const orgData = await orgResponse.json();
            // Ensure all organizations have valid IDs and names
            const validOrgData = orgData.filter((org) => org && org.id && org.name);

            setOrganizations(validOrgData.reverse()); // Reverse to show newest first
          }
        }

        // Fetch business units - for regular admin only (super admin will fetch when org is selected)
        if (!isSuperAdminUser) {
          const userDataJSON = localStorage.getItem("token");
          if (userDataJSON) {
            const { organizationId } = JSON.parse(userDataJSON);
            const { getBusinessUnits } = await import("../../Utility/utility");
            const buData = await getBusinessUnits(organizationId);
            setBusinessUnits(buData.filter((row) => row.isDeleted === false));
          }
        }
      } catch (error) {
        console.error("Error initializing data:", error);
      }
    };

    initializeData();
  }, [isSuperAdmin]);

  React.useEffect(() => {
    if (handleEdit) {
      setFirstName(handleEdit.firstName || "");
      setLastName(handleEdit.lastName || "");
      setEmail(handleEdit.email || "");

      // Parse mobile number to extract country code and number
      const fullMobileNumber = handleEdit.mobileNumber || "";
      if (fullMobileNumber && fullMobileNumber.startsWith("+")) {
        // Find matching country code from COUNTRY_CODES
        let foundCode = null;
        let numberPart = fullMobileNumber;

        // Sort by dial code length (longest first) to match longest codes first
        const sortedCodes = [...COUNTRY_CODES].sort(
          (a, b) => b.dialCode.length - a.dialCode.length
        );

        for (const country of sortedCodes) {
          if (fullMobileNumber.startsWith(country.dialCode)) {
            foundCode = country.dialCode;
            numberPart = fullMobileNumber.substring(country.dialCode.length);
            break;
          }
        }

        setCountryCode(foundCode || "+44");
        setMobileNumber(numberPart);
      } else {
        setMobileNumber(fullMobileNumber);
        setCountryCode("+44"); // Default to UK
      }

      setIsSuspended(handleEdit.isSuspended === false ? true : false);
      setMfaEnabled(handleEdit.mfaEnabled || false);
      setMfaEmail(handleEdit.mfaEmail || false);
      setMfaMobile(handleEdit.mfaMobile || false);
      setMaxConcurrentSessions(handleEdit.maxConcurrentSessions || 1);
      setIsSystemAdmin(
        handleEdit.userRoles?.some((role) =>
          roles.find((r) => r.id === role.id && r.name === "Admin")
        )
      );
      setIsMaker(
        handleEdit.userRoles?.some((role) =>
          roles.find((r) => r.id === role.id && r.name === "Maker")
        )
      );
      setIsChecker(
        handleEdit.userRoles?.some((role) =>
          roles.find((r) => r.id === role.id && r.name === "Checker")
        )
      );
      setSelectedBusinessUnits(
        businessUnits.filter((unit) =>
          handleEdit.businessUnits?.some((editUnit) => editUnit.id === unit.id)
        )
      );

      // Set default business unit when editing
      const defaultUnit = handleEdit.businessUnits?.find((unit) => unit.isDefault);
      if (defaultUnit) {
        setDefaultBusinessUnitId(defaultUnit.id);
      }

      // Handle organization selection when editing a user
      if (isSuperAdmin) {
        if (handleEdit.organizationId && organizations.length > 0) {
          // Find the organization in the list by ID
          const foundOrg = organizations.find((org) => org.id === handleEdit.organizationId);
          if (foundOrg) {
            setSelectedOrganization(foundOrg);
            // Fetch business units for this org without wiping the assignment below
            fetchBusinessUnitsForOrganization(foundOrg.id, { resetSelection: false });
          }
        }
      }
    } else {
      setFirstName("");
      setLastName("");
      setEmail("");
      setMobileNumber("");
      setCountryCode("+44"); // Reset to default country code
      setPassword(""); // Clear password field
      setIsSystemAdmin(false);
      setIsMaker(false);
      setIsChecker(false);
      setMfaEnabled(false);
      setMfaEmail(false);
      setMfaMobile(false);
      setMaxConcurrentSessions(1);
      setSelectedBusinessUnits([]);
      setSelectedOrganization(null);
      setDefaultBusinessUnitId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleEdit, isSuperAdmin, organizations]);

  const disableOtherRoles = (role) => {
    if (role === "Admin") {
      setIsMaker(false);
      setIsChecker(false);
    } else {
      setIsSystemAdmin(false);
    }
  };

  // Fetch business units for selected organization (super admin only)
  const fetchBusinessUnitsForOrganization = async (
    organizationId,
    { resetSelection = true } = {}
  ) => {
    try {
      const { getBusinessUnits } = await import("../../Utility/utility");
      const buData = await getBusinessUnits(organizationId);
      const filteredBusinessUnits = buData.filter((row) => row.isDeleted === false);
      setBusinessUnits(filteredBusinessUnits);

      if (resetSelection) {
        // Organization was changed manually — the old selection no longer applies
        setSelectedBusinessUnits([]);
        setDefaultBusinessUnitId(null);
      } else if (handleEdit) {
        // Restore the user's previously assigned units now that they've loaded
        setSelectedBusinessUnits(
          filteredBusinessUnits.filter((unit) =>
            handleEdit.businessUnits?.some((editUnit) => editUnit.id === unit.id)
          )
        );
        const defaultUnit = handleEdit.businessUnits?.find((unit) => unit.isDefault);
        setDefaultBusinessUnitId(defaultUnit ? defaultUnit.id : null);
      }
    } catch (error) {
      console.error("Error fetching business units for organization:", error);
      setBusinessUnits([]);
      setSelectedBusinessUnits([]);
      setDefaultBusinessUnitId(null);
    }
  };

  return (
    <Modal open={show} onClose={handleClose} aria-labelledby="add-user-modal">
      <div className="user-modal modal-container" style={{ minWidth: 600 }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "24px",
          }}
        >
          <span className="modal-title" style={{ fontWeight: 500 }}>
            {title}
          </span>
          <button onClick={handleClose} className="modal-close-btn" aria-label="Close">
            &#x2715;
          </button>
        </div>

        <div>
          {/* First Name + Last Name — side by side */}
          <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <label className="modal-label">First Name</label>
              <InputBase
                className="modal-input"
                style={{ border: errors.firstName ? "1px solid #f44336" : "1px solid #e0e0e0" }}
                inputProps={{ style: { padding: 0 } }}
                value={firstName}
                placeholder="Jane"
                onChange={(e) => {
                  setFirstName(e.target.value);
                  if (errors.firstName && e.target.value.trim())
                    setErrors({ ...errors, firstName: "" });
                }}
              />
              {errors.firstName && (
                <span style={{ color: "#f44336", fontSize: "12px", marginTop: "4px" }}>
                  {errors.firstName}
                </span>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <label className="modal-label">Last Name</label>
              <InputBase
                className="modal-input"
                style={{ border: errors.lastName ? "1px solid #f44336" : "1px solid #e0e0e0" }}
                inputProps={{ style: { padding: 0 } }}
                value={lastName}
                placeholder="Smith"
                onChange={(e) => {
                  setLastName(e.target.value);
                  if (errors.lastName && e.target.value.trim())
                    setErrors({ ...errors, lastName: "" });
                }}
              />
              {errors.lastName && (
                <span style={{ color: "#f44336", fontSize: "12px", marginTop: "4px" }}>
                  {errors.lastName}
                </span>
              )}
            </div>
          </div>

          {/* Email Address */}
          <div className="modal-field-wrap">
            <label className="modal-label">Email Address</label>
            <InputBase
              className="modal-input"
              style={{ border: errors.email ? "1px solid #f44336" : "1px solid #e0e0e0" }}
              inputProps={{ style: { padding: 0 } }}
              value={email}
              placeholder="jane@company.com"
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.target.value))
                  setErrors({ ...errors, email: "" });
              }}
            />
            {errors.email && (
              <span style={{ color: "#f44336", fontSize: "12px", marginTop: "4px" }}>
                {errors.email}
              </span>
            )}
          </div>

          {/* Username / Phone */}
          <div className="modal-field-wrap">
            <label className="modal-label">Username / Phone</label>
            <div style={{ display: "flex", gap: "8px" }}>
              <Autocomplete
                options={getCountryOptions()}
                value={getCountryOptions().find((opt) => opt.value === countryCode) || null}
                onChange={(event, newValue) => setCountryCode(newValue ? newValue.value : "")}
                getOptionLabel={(option) => option.value || ""}
                renderOption={(props, option) => (
                  <li {...props} style={{ fontFamily: fontFamily, fontSize: 14 }}>
                    {option.label}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      className: "modal-dropdown-input",
                      style: {
                        width: "80px",
                        border: "1px solid #E5E7EB",
                      },
                    }}
                  />
                )}
                ListboxProps={{ style: { maxHeight: 300, fontFamily: fontFamily } }}
                noOptionsText="No countries found"
                isOptionEqualToValue={(option, value) => option.value === value.value}
                style={{ width: "100px", flexShrink: 0 }}
                disableClearable
              />
              <InputBase
                className="modal-input"
                style={{
                  flex: 1,
                  border: errors.mobileNumber ? "1px solid #f44336" : "1px solid #e0e0e0",
                }}
                inputProps={{ style: { padding: 0 }, maxLength: 15 }}
                placeholder="7700 000000"
                value={mobileNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  setMobileNumber(value);
                  if (errors.mobileNumber && value.trim())
                    setErrors({ ...errors, mobileNumber: "" });
                }}
              />
            </div>
            {errors.mobileNumber && (
              <span style={{ color: "#f44336", fontSize: "12px", marginTop: "4px" }}>
                {errors.mobileNumber}
              </span>
            )}
          </div>

          {/* Password (create only) */}
          {!handleEdit && (
            <div className="modal-field-wrap">
              <label className="modal-label">Password</label>
              <InputBase
                className="modal-input"
                style={{ border: errors.password ? "1px solid #f44336" : "1px solid #e0e0e0" }}
                inputProps={{ style: { padding: 0 }, autoComplete: "new-password" }}
                type="password"
                value={password}
                name="new-password"
                autoComplete="new-password"
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) {
                    const pwd = e.target.value;
                    if (
                      pwd.length >= 8 &&
                      /[A-Z]/.test(pwd) &&
                      /[0-9]/.test(pwd) &&
                      /[!@#$%^&*]/.test(pwd)
                    ) {
                      setErrors({ ...errors, password: "" });
                    }
                  }
                }}
              />
              {password.length > 0 &&
                !(
                  password.length >= 8 &&
                  /[A-Z]/.test(password) &&
                  /[0-9]/.test(password) &&
                  /[!@#$%^&*]/.test(password)
                ) && (
                  <div style={{ marginTop: "5px", fontSize: "12px", color: "#666" }}>
                    <div style={{ marginBottom: "2px" }}>Password must contain:</div>
                    <div style={{ color: password.length >= 8 ? "#4caf50" : "#f44336" }}>
                      ✓ At least 8 characters
                    </div>
                    <div style={{ color: /[A-Z]/.test(password) ? "#4caf50" : "#f44336" }}>
                      ✓ One uppercase letter
                    </div>
                    <div style={{ color: /[0-9]/.test(password) ? "#4caf50" : "#f44336" }}>
                      ✓ One number
                    </div>
                    <div style={{ color: /[!@#$%^&*]/.test(password) ? "#4caf50" : "#f44336" }}>
                      ✓ One special character (!@#$%^&*)
                    </div>
                  </div>
                )}
              {errors.password && (
                <span style={{ color: "#f44336", fontSize: "12px", marginTop: "4px" }}>
                  {errors.password}
                </span>
              )}
            </div>
          )}

          {/* Super Admin: Organization */}
          {isSuperAdmin && (
            <div className="modal-field-wrap">
              <label className="modal-label">
                Organization <span style={{ color: "red" }}>*</span>
              </label>
              <Autocomplete
                options={organizations}
                getOptionLabel={(option) => option.name || ""}
                value={selectedOrganization}
                onChange={(event, newValue) => {
                  setSelectedOrganization(newValue);
                  if (newValue && newValue.id) fetchBusinessUnitsForOrganization(newValue.id);
                  else {
                    setBusinessUnits([]);
                    setSelectedBusinessUnits([]);
                    setDefaultBusinessUnitId(null);
                  }
                }}
                inputValue={organizationSearchValue}
                onInputChange={(event, newInputValue) => setOrganizationSearchValue(newInputValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    placeholder="Search organization..."
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      className: "modal-dropdown-input",
                      style: {
                        height: "44px",
                        border: errors.organization ? "1px solid #f44336" : "1px solid #E5E7EB",
                      },
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                noOptionsText="No organizations found"
                fullWidth
              />
              {errors.organization && (
                <span style={{ color: "#f44336", fontSize: "12px", marginTop: "4px" }}>
                  {errors.organization}
                </span>
              )}
            </div>
          )}

          {/* Client + Role — side by side */}
          <div
            style={{ display: "flex", gap: "16px", marginBottom: "16px", alignItems: "flex-start" }}
          >
            {/* Client */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minWidth: 0,
                marginBottom: 0,
              }}
            >
              <label className="modal-label">
                Client <span style={{ color: "red" }}>*</span>
              </label>
              <Autocomplete
                multiple
                disableClearable
                options={businessUnits}
                getOptionLabel={(option) => formatBusinessUnitLabel(option)}
                value={selectedBusinessUnits}
                onChange={(e, value) => {
                  setSelectedBusinessUnits(value);
                  if (errors.businessUnits && value.length > 0)
                    setErrors({ ...errors, businessUnits: "" });
                  if (value.length === 0) {
                    setDefaultBusinessUnitId(null);
                  } else {
                    // Always set the most recently selected item as default
                    setDefaultBusinessUnitId(value[value.length - 1].id);
                    if (errors.defaultBusinessUnit)
                      setErrors((prev) => ({ ...prev, defaultBusinessUnit: "" }));
                  }
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      key={index}
                      {...getTagProps({ index })}
                      label={option.name}
                      size="small"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    placeholder={selectedBusinessUnits.length === 0 ? "Select" : ""}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      className: "modal-dropdown-input",
                      style: {
                        minHeight: "44px",
                        // Cap chip area height and scroll internally so the field doesn't push the Role column
                        maxHeight: "88px",
                        overflowY: "auto",
                        // Reserve space for the clear/dropdown icons so chips don't overlap the close button
                        paddingRight: "56px",
                        border: errors.businessUnits ? "1px solid #f44336" : "1px solid #E5E7EB",
                      },
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                fullWidth
              />
              {errors.businessUnits && (
                <span style={{ color: "#f44336", fontSize: "12px", marginTop: "4px" }}>
                  {errors.businessUnits}
                </span>
              )}
              {errors.defaultBusinessUnit && (
                <span style={{ color: "#f44336", fontSize: "12px", marginTop: "4px" }}>
                  {errors.defaultBusinessUnit}
                </span>
              )}
            </div>

            {/* Role */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minWidth: 0,
                marginBottom: 0,
              }}
            >
              <label className="modal-label">
                Role <span style={{ color: "red" }}>*</span>
              </label>
              <div
                style={{
                  minHeight: "44px",
                  backgroundColor: "#F9FAFB",
                  borderRadius: "10px",
                  border: errors.roles ? "1px solid #f44336" : "1px solid #E5E7EB",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "0 10px",
                  flexWrap: "nowrap",
                }}
              >
                {[
                  {
                    label: "Admin",
                    checked: isSystemAdmin,
                    onChange: (e) => {
                      setIsSystemAdmin(e.target.checked);
                      disableOtherRoles("Admin");
                      if (errors.roles && e.target.checked) setErrors({ ...errors, roles: "" });
                    },
                    disabled: isMaker || isChecker,
                  },
                  {
                    label: "Maker",
                    checked: isMaker,
                    onChange: (e) => {
                      setIsMaker(e.target.checked);
                      disableOtherRoles("Maker");
                      if (errors.roles && e.target.checked) setErrors({ ...errors, roles: "" });
                    },
                    disabled: isSystemAdmin,
                  },
                  {
                    label: "Checker",
                    checked: isChecker,
                    onChange: (e) => {
                      setIsChecker(e.target.checked);
                      disableOtherRoles("Checker");
                      if (errors.roles && e.target.checked) setErrors({ ...errors, roles: "" });
                    },
                    disabled: isSystemAdmin,
                  },
                ].map(({ label, checked, onChange, disabled }) => (
                  <span
                    key={label}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      opacity: disabled ? 0.5 : 1,
                    }}
                  >
                    <AppCheckbox checked={checked} onChange={onChange} disabled={disabled} />
                    <span
                      onClick={() =>
                        !disabled &&
                        onChange({
                          type: "change",
                          target: { checked: !checked },
                          stopPropagation: () => {},
                          preventDefault: () => {},
                        })
                      }
                      style={{
                        fontSize: "13px",
                        fontFamily: fontFamily,
                        cursor: disabled ? "not-allowed" : "pointer",
                        userSelect: "none",
                      }}
                    >
                      {label}
                    </span>
                  </span>
                ))}
              </div>
              {errors.roles && (
                <span style={{ color: "#f44336", fontSize: "12px", marginTop: "4px" }}>
                  {errors.roles}
                </span>
              )}
            </div>
          </div>

          {/* Security & Notifications */}
          <div style={{ marginTop: "8px", marginBottom: "20px" }}>
            <div
              style={{
                fontSize: "11px",
                fontWeight: 600,
                color: "#999",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                marginBottom: "10px",
                fontFamily: fontFamily,
              }}
            >
              Security &amp; Notifications
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#F9FAFB",
                borderRadius: "12px",
                padding: "14px 16px",
                gap: "14px",
              }}
            >
              <img
                src={MfaIcon}
                alt="MFA"
                style={{ width: "24px", height: "24px", flexShrink: 0 }}
              />
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#222",
                    fontFamily: fontFamily,
                  }}
                >
                  Multi-Factor Authentication
                </div>
                <div style={{ fontSize: "12px", color: "#888", fontFamily: fontFamily }}>
                  Extra layer of login security
                </div>
              </div>
              <Switch
                checked={mfaEnabled}
                onChange={(e) => {
                  const val = e.target.checked;
                  setMfaEnabled(val);
                  setMfaEmail(val);
                  setMfaMobile(false);
                }}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "var(--checkbox-active-color, #98ca49)",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "var(--checkbox-active-color, #98ca49)",
                  },
                }}
              />
            </div>

            {/* Suspend / Activate toggle */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#F9FAFB",
                borderRadius: "12px",
                padding: "14px 16px",
                gap: "14px",
                marginTop: "10px",
              }}
            >
              <div
                style={{
                  width: "24px",
                  height: "24px",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  backgroundColor: isSuspended ? "#e6f7ed" : "#fdecea",
                  fontSize: "14px",
                }}
              >
                {isSuspended ? "✓" : "⊘"}
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#222",
                    fontFamily: fontFamily,
                  }}
                >
                  Account Status
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: isSuspended ? "#4caf50" : "#f44336",
                    fontFamily: fontFamily,
                  }}
                >
                  {isSuspended ? "Active — user can log in" : "Suspended — user cannot log in"}
                </div>
              </div>
              <Switch
                checked={isSuspended}
                onChange={(e) => setIsSuspended(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#4caf50" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#4caf50",
                  },
                  "& .MuiSwitch-switchBase:not(.Mui-checked)": { color: "#f44336" },
                  "& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track": {
                    backgroundColor: "#f44336",
                  },
                }}
              />
            </div>
          </div>

          {/* Buttons */}
          <div
            style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "4px" }}
          >
            <Button variant="contained" onClick={handleClose} className="cancel-btn-filled">
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSaveClick} className="review-btn-filled">
              Save changes
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
